import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
} from 'react';
import { Area, AreaChart, CartesianGrid, ReferenceLine, XAxis, YAxis } from 'recharts';
import type { ShopifyConversionsRange, ShopifyConversionsSeriesBucket } from '@discovr/types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../components/ui/chart';

export type RangePreset = '7d' | '30d' | '90d' | 'all' | 'custom';

export const RANGE_PRESETS: { key: RangePreset; label: string; days: number | null }[] = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
];

const utcDay = (d: Date): string => d.toISOString().slice(0, 10);

/** Inclusive UTC days: to = today, from = today minus (N-1). All time sends neither. */
export function rangeForPreset(
  preset: RangePreset,
  now: Date = new Date(),
): ShopifyConversionsRange {
  const days = RANGE_PRESETS.find((p) => p.key === preset)?.days;
  if (!days) return {};
  const from = new Date(now.getTime() - (days - 1) * 86_400_000);
  return { from: utcDay(from), to: utcDay(now) };
}

export function RangeControl({
  value,
  range,
  onChange,
  onApply,
}: {
  value: RangePreset;
  range: ShopifyConversionsRange;
  onChange: (next: RangePreset) => void;
  onApply: (range: ShopifyConversionsRange) => void;
}) {
  const id = useId();
  const [from, setFrom] = useState(range.from ?? '');
  const [to, setTo] = useState(range.to ?? '');
  const invalid = !!from && !!to && from > to;
  const todayUtc = utcDay(new Date());
  return (
    <div role="group" aria-label="Date range" className="flex max-w-full flex-col items-end gap-3">
      <Select
        value={value}
        onValueChange={(next) => {
          if (next === 'custom') {
            setFrom(range.from ?? '');
            setTo(range.to ?? utcDay(new Date()));
          }
          onChange(next as RangePreset);
        }}
      >
        <SelectTrigger aria-label="Date range" size="sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {RANGE_PRESETS.map((p) => (
            <SelectItem key={p.key} value={p.key}>
              {p.label}
            </SelectItem>
          ))}
          <SelectItem value="custom">
            Custom dates
          </SelectItem>
        </SelectContent>
      </Select>
      {value === 'custom' ? (
        <form
          className="flex max-w-full flex-wrap items-end justify-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (from && to && !invalid) onApply({ from, to });
          }}
        >
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`${id}-from`} className="text-caption-regular text-muted-foreground">
              From (UTC)
            </Label>
            <Input
              id={`${id}-from`}
              type="date"
              required
              value={from}
              max={to || todayUtc}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 w-36 text-caption-regular"
            />
          </div>
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`${id}-to`} className="text-caption-regular text-muted-foreground">
              To (UTC)
            </Label>
            <Input
              id={`${id}-to`}
              type="date"
              required
              value={to}
              min={from || undefined}
              max={todayUtc}
              onChange={(e) => setTo(e.target.value)}
              aria-invalid={invalid}
              aria-describedby={invalid ? `${id}-error` : undefined}
              className="h-8 w-36 text-caption-regular"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" disabled={!from || !to || invalid}>
            Apply
          </Button>
          {invalid ? (
            <p
              id={`${id}-error`}
              role="alert"
              className="text-destructive w-full text-right text-caption-regular"
            >
              To must be on or after From.
            </p>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

const weekFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const weekLabel = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : weekFmt.format(d);
};
const NARROW_QUERY = '(max-width: 390px)';
const NARROW_WEEKS = 26;
const WEEK_MS = 7 * 86_400_000;

const subscribeNarrow = (onChange: () => void): (() => void) => {
  if (typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(NARROW_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
};
const narrowSnapshot = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
const useNarrow = (): boolean => useSyncExternalStore(subscribeNarrow, narrowSnapshot, () => false);

function lastCompleteIndex(buckets: ShopifyConversionsSeriesBucket[], now: Date): number {
  const last = buckets.length - 1;
  const start = Date.parse(`${buckets[last]?.week_start}T00:00:00Z`);
  if (last > 0 && !Number.isNaN(start) && start + WEEK_MS > now.getTime()) return last - 1;
  return Math.max(last, 0);
}

const intFmt = new Intl.NumberFormat('en-US');
const config: ChartConfig = { net_gmv: { label: 'Net sales', color: 'var(--brand-600)' } };

interface SalesInteraction {
  buckets: ShopifyConversionsSeriesBucket[];
  tabStop: number;
  describe: (bucket: ShopifyConversionsSeriesBucket) => string;
  pointRef: (index: number, el: SVGGElement | null) => void;
  onSelect: (index: number) => void;
  onFocus: (index: number) => void;
  onBlur: () => void;
  onKeyDown: (e: KeyboardEvent<SVGGElement>, index: number) => void;
}
const SalesInteractionContext = createContext<SalesInteraction | null>(null);
function SalesPoint({ cx, cy, index }: { cx?: number; cy?: number; index?: number }) {
  const interaction = useContext(SalesInteractionContext);
  if (!interaction || cx === undefined || cy === undefined || index === undefined) return <g />;
  const { buckets, tabStop, describe, pointRef, onSelect, onFocus, onBlur, onKeyDown } =
    interaction;
  const b = buckets[index];
  if (!b) return <g />;
  return (
    <g
      ref={(el) => pointRef(index, el)}
      tabIndex={tabStop === index ? 0 : -1}
      role="button"
      aria-label={`Week of ${weekLabel(b.week_start)}: ${describe(b)}`}
      className="[&:focus-visible>circle]:stroke-ring outline-none [&:focus-visible>circle]:stroke-2"
      onMouseEnter={() => onSelect(index)}
      onKeyDown={(e) => onKeyDown(e, index)}
      onFocus={() => onFocus(index)}
      onBlur={onBlur}
      onClick={() => onSelect(index)}
    >
      <circle cx={cx} cy={cy} r={4} fill="var(--color-net_gmv)" stroke="var(--card)" />
    </g>
  );
}

export function SalesTrendChart({
  buckets: allBuckets,
  fmtMoney,
  now,
}: {
  buckets: ShopifyConversionsSeriesBucket[];
  fmtMoney: (n: number) => string;
  now?: Date;
}) {
  const narrow = useNarrow();
  const capped = narrow && allBuckets.length > NARROW_WEEKS;
  const buckets = capped ? allBuckets.slice(-NARROW_WEEKS) : allBuckets;
  const [hover, setHover] = useState<number | null>(null);
  const [stop, setStop] = useState<number | null>(null);
  const focusInside = useRef(false);
  const seriesKey = `${allBuckets[0]?.week_start ?? ''}|${allBuckets[allBuckets.length - 1]?.week_start ?? ''}|${allBuckets.length}`;
  useEffect(() => setStop(null), [seriesKey]);
  const points = useRef<(SVGGElement | null)[]>([]);
  const onMouseLeave = useCallback(() => {
    if (!focusInside.current) setHover(null);
  }, []);
  const first = buckets[0];
  if (!first)
    return (
      <div className="text-muted-foreground flex h-[260px] items-center justify-center text-body-sm-regular">
        No sales data for this period.
      </div>
    );
  const n = buckets.length;
  const total = buckets.reduce((a, b) => a + b.net_gmv, 0);
  const peak = buckets.reduce((a, b) => (b.net_gmv > a.net_gmv ? b : a), first);
  const defaultIndex = lastCompleteIndex(buckets, now ?? new Date());
  const active = buckets[Math.min(hover ?? defaultIndex, n - 1)] ?? first;
  const tabStop = Math.min(stop ?? defaultIndex, n - 1);
  const describe = (b: ShopifyConversionsSeriesBucket, sep = ', '): string =>
    [
      `${fmtMoney(b.net_gmv)} net sales`,
      `${intFmt.format(b.orders)} ${b.orders === 1 ? 'order' : 'orders'}`,
      `${intFmt.format(b.visits)} ${b.visits === 1 ? 'visit' : 'visits'}`,
    ].join(sep);
  const onKeyDown = (e: KeyboardEvent<SVGGElement>, i: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setHover(i);
      return;
    }
    const next =
      e.key === 'ArrowRight'
        ? Math.min(i + 1, n - 1)
        : e.key === 'ArrowLeft'
          ? Math.max(i - 1, 0)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? n - 1
              : null;
    if (next === null) return;
    e.preventDefault();
    points.current[next]?.focus();
  };
  const summary = `Weekly net sales, ${weekLabel(first.week_start)} to ${weekLabel((buckets[n - 1] ?? first).week_start)}: ${fmtMoney(total)} in total, highest week of ${weekLabel(peak.week_start)} at ${fmtMoney(peak.net_gmv)}.`;
  return (
    <div className="min-w-0" role="group" aria-label={summary}>
      <SalesInteractionContext.Provider
        value={{
          buckets,
          tabStop,
          describe,
          pointRef: (i, el) => {
            points.current[i] = el;
          },
          onSelect: setHover,
          onFocus: (i) => {
            focusInside.current = true;
            setStop(i);
            setHover(i);
          },
          onBlur: () => {
            focusInside.current = false;
            setHover(null);
          },
          onKeyDown,
        }}
      >
        <SalesPlot buckets={buckets} fmtMoney={fmtMoney} onMouseLeave={onMouseLeave} />
      </SalesInteractionContext.Provider>
      {capped ? (
        <p className="text-muted-foreground mt-2 text-caption-regular">
          Showing the latest {NARROW_WEEKS} weeks
        </p>
      ) : null}
      <p className="text-muted-foreground mt-3 text-caption-regular tabular-nums" aria-hidden="true">
        Week of {weekLabel(active.week_start)}: {describe(active, ' · ')}
      </p>
      <div className="sr-only">
        <table>
          <caption>Weekly net sales, paid orders and referral visits</caption>
          <thead>
            <tr>
              <th scope="col">Week of</th>
              <th scope="col">Net sales</th>
              <th scope="col">Paid orders</th>
              <th scope="col">Referral visits</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={b.week_start}>
                <th scope="row">{weekLabel(b.week_start)}</th>
                <td>{fmtMoney(b.net_gmv)}</td>
                <td>{intFmt.format(b.orders)}</td>
                <td>{intFmt.format(b.visits)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Keep Recharts' keyed animation layer stable while keyboard focus changes.
const SalesPlot = memo(function SalesPlot({
  buckets,
  fmtMoney,
  onMouseLeave,
}: {
  buckets: ShopifyConversionsSeriesBucket[];
  fmtMoney: (value: number) => string;
  onMouseLeave: () => void;
}) {
  return (
    <div className="h-[260px]">
      <ChartContainer config={config} className="size-full">
        <AreaChart
          data={buckets}
          margin={{ top: 12, right: 16, bottom: 4, left: 0 }}
          accessibilityLayer={false}
          onMouseLeave={onMouseLeave}
        >
          <CartesianGrid stroke="var(--color-stone-200)" vertical={false} />
          <XAxis
            dataKey="week_start"
            axisLine={false}
            tickLine={false}
            tickMargin={12}
            minTickGap={24}
            tickFormatter={(v: string) => weekLabel(v).replace(/, \d{4}$/, '')}
            tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            width={68}
            tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
            tickFormatter={fmtMoney}
            domain={['auto', 'auto']}
          />
          <ReferenceLine y={0} stroke="var(--border)" />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(v) => `Week of ${weekLabel(String(v))} (UTC)`}
                formatter={(value) => fmtMoney(Number(value))}
              />
            }
          />
          <Area
            type="monotone"
            dataKey="net_gmv"
            stroke="var(--color-net_gmv)"
            strokeWidth={2}
            fill="none"
            isAnimationActive={false}
            dot={<SalesPoint />}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
});
